import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowSquareOut, ClockCounterClockwise, Copy, PencilSimple } from "@phosphor-icons/react";
import PositionDialog from "@/pages/admin/hiring/PositionDialog";

const TONE = { draft: "border-slate-300", open: "border-emerald-400", paused: "border-amber-400",
               closed: "border-slate-400", archived: "border-slate-200" };
const MOVES = { draft: [["open", "Publish"], ["archived", "Archive"]],
                open: [["paused", "Pause"], ["closed", "Close"]],
                paused: [["open", "Resume"], ["closed", "Close"]],
                closed: [["open", "Reopen"], ["archived", "Archive"]],
                archived: [["draft", "Restore to draft"]] };

export default function PositionsTab({ meta }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [editing, setEditing] = useState(null);
  const [history, setHistory] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/hiring/positions", { params: status === "all" ? {} : { status } });
      setRows(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const move = async (p, next) => {
    const reason = window.prompt(`Reason for this change to "${p.title}"? (optional)`) ?? "";
    try {
      await api.post(`/hiring/positions/${p.id}/status`, { status: next, reason });
      toast.success(`"${p.title}" updated — applications are preserved`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const duplicate = async (p) => {
    try { await api.post(`/hiring/positions/${p.id}/duplicate`); toast.success("Duplicated as a new draft"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openHistory = async (p) => {
    try { const { data } = await api.get(`/hiring/positions/${p.id}/history`); setHistory(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="positions-tab">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-11 rounded-xl w-56 bg-white" data-testid="position-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All except archived</SelectItem>
            {meta.position_statuses.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {meta.can.postings_edit && (
          <Button onClick={() => setEditing({})} className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                  data-testid="new-position-btn">Create a position</Button>
        )}
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2" data-testid="positions-list">
        {rows.length === 0 && (
          <p className="text-muted-foreground" data-testid="no-positions">
            No postings yet. Create one as a draft — nothing appears on the public careers page until you publish it.
          </p>
        )}
        {rows.map((p) => (
          <div key={p.id} className={`bg-white rounded-2xl border-2 p-6 ${TONE[p.status] || "border-border"}`}
               data-testid={`position-row-${p.id}`}>
            <div className="flex justify-between gap-3 items-start">
              <div>
                <p className="font-display text-lg font-semibold">{p.title}</p>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#3B82F6] mt-1">
                  {p.role_label} · {p.employment_type}
                </p>
              </div>
              <span className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-slate-100"
                    data-testid={`position-status-${p.id}`}>{p.status}</span>
            </div>
            <dl className="mt-4 space-y-1.5 text-sm">
              {[["Location", p.location || "—"], ["Schedule", p.schedule || "—"],
                ["Pay published", p.pay_published ? "Yes" : "No"], ["Deadline", p.deadline || "None"],
                ["Openings", p.openings], ["Applications", p.application_count],
                ["Accepting now", p.accepting ? "Yes" : "No"]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <dt className="text-muted-foreground font-semibold">{k}</dt><dd className="font-semibold">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-5 flex flex-wrap gap-2">
              {meta.can.postings_edit && p.status !== "archived" && (
                <Button size="sm" variant="outline" className="rounded-full font-bold" data-testid={`edit-position-${p.id}`}
                        onClick={() => setEditing(p)}><PencilSimple className="mr-1" /> Edit</Button>
              )}
              <Button size="sm" variant="outline" className="rounded-full font-bold" asChild>
                <a href={`/careers/${p.id}`} target="_blank" rel="noreferrer" data-testid={`preview-position-${p.id}`}>
                  <ArrowSquareOut className="mr-1" /> Preview
                </a>
              </Button>
              {meta.can.postings_publish && (MOVES[p.status] || []).map(([next, label]) => (
                <Button key={next} size="sm" variant="outline" className="rounded-full font-bold"
                        data-testid={`move-${next}-${p.id}`} onClick={() => move(p, next)}>{label}</Button>
              ))}
              {meta.can.postings_edit && (
                <Button size="sm" variant="outline" className="rounded-full font-bold" data-testid={`duplicate-position-${p.id}`}
                        onClick={() => duplicate(p)}><Copy className="mr-1" /> Duplicate</Button>
              )}
              <Button size="sm" variant="outline" className="rounded-full font-bold" data-testid={`history-position-${p.id}`}
                      onClick={() => openHistory(p)}><ClockCounterClockwise className="mr-1" /> History</Button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <PositionDialog meta={meta} position={editing} onClose={() => setEditing(null)}
                        onSaved={() => { setEditing(null); load(); }} />
      )}

      <Dialog open={Boolean(history)} onOpenChange={() => setHistory(null)}>
        <DialogContent className="max-w-xl" data-testid="position-history-dialog">
          <DialogHeader><DialogTitle>Change history — {history?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {(history?.history || []).map((h, i) => (
              <div key={`${h.at}-${i}`} className="text-sm border-b border-border pb-2">
                <p className="font-bold capitalize">{h.action}{h.to_status ? ` · status now ${h.to_status}` : ""}</p>
                <p className="text-muted-foreground">{new Date(h.at).toLocaleString()} · {h.by}</p>
                {h.from_status && h.from_status !== h.to_status && (
                  <p className="text-xs text-muted-foreground">was {h.from_status}</p>
                )}
                {h.note && <p className="mt-1">{h.note}</p>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
